import { inject, Injectable } from '@angular/core';
import {
  catchError,
  debounceTime,
  delay,
  EMPTY,
  filter,
  map,
  of,
  repeat,
  switchMap,
  tap,
} from 'rxjs';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import * as uuid from 'uuid';
import { getSelectedProjectId } from '../projectDocuments/projectDocuments.selectors';
import { primeChangesSidebarActions } from '../prime-commitments/prime-commitments.actions';
import { PCO_LISTING_PER_PAGE_BASE } from '../prime-commitments/prime-commitments.constants';
import { viewProjectSelectors } from '../view-project/view-project.selectors';
import { pcoActions } from './pco.actions';
import { pcoSelectors } from './pco.selectors';
import { PcoDetail, PcoLogMetadata } from './pco.types';
import { pcoListingActions } from '@app/pages/webapp/projects/view-project/pco-listing/pco-listing.component.actions';
import { CommitmentsService } from '@app/services/commitments.service';
import { NotificationV2Service } from '@app/notification-v2.service';
import { PrimeCommitmentsApiService } from '@app/services/prime-commitments-api.service';
import { InteractionBarStateService } from '@app/services/interaction-bar-state.service';
import { DeepCopyService } from '@app/services/deep-copy.service';
import { PresignedFileUploadService } from '@app/services/presigned-file-upload.service';

@Injectable()
export class PcoEffects {
  private actions$ = inject(Actions);
  private commitmentsService = inject(CommitmentsService);
  private notif = inject(NotificationV2Service);
  private store = inject(Store);
  private primeCommitmentsApiService = inject(PrimeCommitmentsApiService);
  private interactionBar = inject(InteractionBarStateService);
  private presignedFileUploadService = inject(PresignedFileUploadService);

  loadPcoNumberOnChangeActionSidebarOpen$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(primeChangesSidebarActions.onChangeActionSidebarOpen),
      concatLatestFrom(() => this.store.select(viewProjectSelectors.selectProjectId)),
      switchMap(([_, projectId]) => {
        return this.primeCommitmentsApiService.getNextPcoNumber(projectId).pipe(
          map((pcoNumberRequest) =>
            pcoActions.nextPcoNumberLoadedSuccessfully({
              nextPcoNumber: pcoNumberRequest.next_id,
            }),
          ),
          catchError((err) => {
            this.notif.showError(err?.error?.message || 'Could not load next PCO number');
            return of(
              pcoActions.nextPcoNumberLoadFailed({
                error: err,
              }),
            );
          }),
        );
      }),
    );
  });

  loadContractsWithChangeOrders$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(pcoActions.loadContractsWithChangeOrders),
      concatLatestFrom(() => this.store.select(getSelectedProjectId)),
      switchMap(([action, selectedProjectId]) => {
        const projectId = 'projectId' in action ? action.projectId : selectedProjectId;
        return this.commitmentsService.getContractsWithChangeOrders(projectId).pipe(
          map((contractsWithChangeOrders) =>
            pcoActions.loadContractsWithChangeOrdersSuccess({
              contractsWithChangeOrders,
            }),
          ),
          catchError((err) => {
            this.notif.showError(
              err?.error?.message ?? 'Failed to load Contracts with Change Orders',
            );
            console.error(err);
            return of(
              pcoActions.loadContractsWithChangeOrdersFailure({
                error: err?.error?.message ?? err?.message ?? 'Unknown error',
              }),
            );
          }),
        );
      }),
    );
  });

  loadContractsOnPCODetailsAccessed$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(pcoActions.onPCODetailsAccessed),
      concatLatestFrom(() => this.store.select(getSelectedProjectId)),
      map(([, selectedProjectId]) =>
        pcoActions.loadContractsWithChangeOrders({ projectId: selectedProjectId }),
      ),
    );
  });

  loadPcoListing$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        // viewProjectActions.selectedProjectChanged,
        // viewProjectActions.refreshNeeded,
        // primeChangesActions.onCORListingAccess, // PCOs have to load because they are used in COR edit sidebar 2nd page
        pcoListingActions.onPCOListingAccess,
        pcoActions.onPCODetailsSidebarClosed,
        pcoActions.onPCODeletedSuccessfully,
        pcoListingActions.onPCOListingStatusChange,
        pcoListingActions.onPCOListingSearch,
        pcoListingActions.onPCOListingNextPageNeeded,
        pcoListingActions.onPCOSortingChange,
        pcoActions.onPCOSaved,
        pcoActions.onPCOChangeActionFirstPageSynced,
        primeChangesSidebarActions.onCORSaved,
      ),
      filter((action) => {
        // if editChangeActionTriggered is true, then don't load PCOs because an another interaction bar was opened
        return !(
          action.type === pcoActions.onPCODetailsSidebarClosed.type &&
          action.editChangeActionTriggered
        );
      }),
      // debounce to avoid multiple requests in a short time AND wait for the reducers to finish updating the state
      debounceTime(200),
      concatLatestFrom(() => [
        this.store.select(viewProjectSelectors.selectProjectId),
        this.store.select(pcoSelectors.selectPCOListingInfo),
      ]),
      switchMap(([action, projectId, pcoListingInfo]) => {
        const requestMetadata: PcoLogMetadata = {
          page: 1,
          perPage: PCO_LISTING_PER_PAGE_BASE,
          statuses: pcoListingInfo.statuses,
          search: pcoListingInfo.search,
          sortBy: pcoListingInfo.sortBy,
          sortDirection: pcoListingInfo.sortDirection,
          isAssigned: null, // we don't care if the PCO is assigned or not in the listing
        };
        if (
          action.type === pcoListingActions.onPCOListingAccess.type &&
          pcoListingInfo.allPcoLogs.length > 0
        ) {
          return EMPTY;
        }

        if (action.type === pcoListingActions.onPCOListingNextPageNeeded.type) {
          requestMetadata.page = pcoListingInfo.lastPage + 1;
          if (pcoListingInfo.lastPageEmpty) {
            return EMPTY;
          }
        }
        if (action.type === pcoListingActions.onPCOListingStatusChange.type) {
          requestMetadata.statuses = action.statuses;
        }
        if (action.type === pcoListingActions.onPCOListingSearch.type) {
          requestMetadata.search = action.search;
        }

        return this.primeCommitmentsApiService.getAllPCOs(projectId, requestMetadata).pipe(
          map((response) => pcoActions.loadPcoListingSuccess({ PCOs: response, requestMetadata })),
          catchError((error) => {
            this.notif.showError(error?.error?.message || 'An error occurred while loading PCOs');
            return of(pcoActions.loadPcoListingFailed());
          }),
        );
      }),
    );
  });

  onRequestSCO$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(pcoActions.onPCORequestSCO),
      switchMap((action) => {
        return this.primeCommitmentsApiService.requestSCO(action.contractId, action.costId);
      }),
      delay(10),
      map((response) => {
        console.warn(response);
        this.notif.showSuccess('SCO Request Created and Sent to Subcontractor for Review');
        return pcoActions.onPcoScoRequested();
      }),
      catchError((error) => {
        this.notif.showError(error?.error?.message || 'An error occurred while deleting PCO');
        return of(pcoActions.onFailedToSavePCO());
      }),
      repeat(),
    );
  });

  onPCODelete$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(pcoActions.onDeletePCO),
      switchMap((action) => {
        return this.primeCommitmentsApiService.deletePCO(action.id);
      }),
      tap(() => {
        this.notif.showSuccess('Successfully deleted');
      }),
      delay(1000),
      map(() => {
        this.interactionBar.close();
        return pcoActions.onPCODeletedSuccessfully();
      }),
      catchError((error) => {
        this.notif.showError(error?.error?.message || 'An error occurred while deleting PCO');
        return of(pcoActions.onFailedToSavePCO());
      }),
      repeat(),
    );
  });

  onPCOSubmit$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(pcoActions.onPCOSubmit),
      concatLatestFrom(() => this.store.select(viewProjectSelectors.selectProjectId)),
      switchMap(([action, projectId]) =>
        this.primeCommitmentsApiService.createPCO(projectId, action.model).pipe(
          map(() => {
            this.notif.showSuccess('Potential change order saved successfully');
            this.interactionBar.close();
            return pcoActions.onPCOSaved();
          }),
          catchError((error) => {
            console.error(error);
            const errorMessage = error?.error?.message || 'An error occurred while submitting';
            this.notif.showError(errorMessage);
            return of(pcoActions.onFailedToSavePCO());
          }),
        ),
      ),
    );
  });

  onPCONumberChange$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(pcoActions.onPCONumberChange),
      concatLatestFrom(() => this.store.select(viewProjectSelectors.selectProjectId)),
      switchMap(([action, projectId]) => {
        return this.primeCommitmentsApiService.isPCONumberUnique(
          projectId,
          action.number,
          action.resource_id,
        );
      }),
      map((_) => pcoActions.PCONumberValidated({ isValid: true })),
      catchError((_) => {
        return of(pcoActions.PCONumberValidated({ isValid: false }));
      }),
      repeat(),
    );
  });

  onLoadPCODetails$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(pcoActions.onPCODetailsAccessed, pcoActions.onPcoScoRequested),
      concatLatestFrom(() => this.store.select(pcoSelectors.selectPCODetailsSidebar)),
      switchMap(([action, sidebar]) => {
        if (action.type === pcoActions.onPcoScoRequested.type) {
          return this.primeCommitmentsApiService.loadPCODetails(sidebar.id);
        }
        return this.primeCommitmentsApiService.loadPCODetails(action.id);
      }),
      map((response) => pcoActions.PCODetailsLoaded({ response: response })),
      catchError((error) => {
        this.notif.showError(
          error?.error?.message || 'An error occurred while loading PCO details',
        );
        return of(pcoActions.onFailedToLoadPCODetails());
      }),
      repeat(),
    );
  });

  onPCODetailsSyncTriggered$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        pcoActions.onSyncPCODetailsTriggered,
        pcoActions.onPCODetailsLocalStateUpdate,
        pcoActions.onPCOLineItemAdded,
      ),
      concatLatestFrom(() => this.store.select(pcoSelectors.selectIsAnyItemBeingEdited)),
      filter(([_, isAnyItemEdited]) => !isAnyItemEdited),
      switchMap(([action, _]) => {
        // allow request to go through only if it should be saved to backend, otherwise cancel it
        if (action.type !== pcoActions.onSyncPCODetailsTriggered.type) {
          return EMPTY;
        }
        return of(pcoActions.onSyncPCODetails({ PCO: action.PCO }));
      }),
    );
  });

  onPCODetailsSyncBackend = createEffect(() => {
    return this.actions$.pipe(
      ofType(pcoActions.onSyncPCODetails),
      concatLatestFrom(() => this.store.select(pcoSelectors.selectIsAnyItemBeingEdited)),
      filter(([_, isAnyItemEdited]) => !isAnyItemEdited),
      switchMap(([action, _]) => {
        const pco = removeLocalIds(action.PCO);
        return this.primeCommitmentsApiService.updatePCODetails(pco);
      }),
      map((response: PcoDetail) => {
        return pcoActions.PCODetailsLoaded({ response: response });
      }),
      catchError((error) => {
        this.notif.showError(error?.error?.message || 'An error occurred while saving PCO');
        return of(pcoActions.onFailedToSavePCODetails());
      }),
      repeat(),
    );
  });

  onUpdatePCOChangeActionSync = createEffect(() => {
    return this.actions$.pipe(
      ofType(pcoActions.onPCOChangeActionFirstPageSync),
      debounceTime(500),
      switchMap((action) => {
        return this.primeCommitmentsApiService.updatePCODetails(action.PCO).pipe(
          map((response: PcoDetail) => {
            return pcoActions.onPCOChangeActionFirstPageSynced({
              response: response,
              model: action.PCO,
            });
          }),
          catchError((error) => {
            this.notif.showError(error?.error?.message || 'An error occurred while saving');
            return of(pcoActions.onFailedToUpdatePCOChangeAction());
          }),
        );
      }),
    );
  });

  manageFilesAfterPCOSave$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(pcoActions.onPCOChangeActionFirstPageSynced),
      switchMap((action) => {
        return this.presignedFileUploadService
          .handleUploadAndDelete(action?.model?.files, { pco_id: action.response.id })
          .pipe(
            map((responses) => {
              this.interactionBar.close();
              if (responses.some((res) => res?.error)) {
                console.error('Error managing PCO files', responses);
                this.notif.showError('An error occurred while updating PCO files');
                return pcoActions.onPCOFilesUploadFailed();
              }
              return pcoActions.onPCOFilesUploadedSuccessfully();
            }),
          );
      }),
    );
  });
}

const removeLocalIds = (pco: PcoDetail) => {
  pco = DeepCopyService.deepCopy(pco);
  pco.pco_items.forEach((item) => {
    if (uuid.validate(item.id)) {
      delete item.id;
    }

    item.pco_item_costs.forEach((cost) => {
      if (uuid.validate(cost.id)) {
        delete cost.id;
      }
    });
  });
  return pco;
};
